import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const TOOLTIP_EDGE_GAP = 8;
const ARROW_EDGE_INSET = 16;

function TooltipPointer({ left }) {
  return (
    <svg
      className="clr-chart-tooltip-pointer"
      width="24"
      height="11"
      viewBox="0 0 24 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={left == null ? undefined : { left: `${left}px` }}
    >
      <path
        d="M12 10.5L2.25 1.75H21.75L12 10.5Z"
        fill="#ffffff"
      />
    </svg>
  );
}

TooltipPointer.propTypes = {
  left: PropTypes.number,
};

function normalizeTooltipContent(payload, label, formatter) {
  const fallbackTitle = label
    ?? payload[0]?.payload?.label
    ?? payload[0]?.payload?.month
    ?? '';

  if (!formatter) {
    return {
      title: fallbackTitle,
      minWidth: null,
      rows: payload.map((entry) => ({
        key: entry.name || entry.dataKey,
        label: entry.name || entry.dataKey,
        value: entry.value,
        color: entry.color || entry.stroke || entry.fill,
      })),
    };
  }

  const result = formatter(payload, label);
  if (!result) {
    return { title: fallbackTitle, minWidth: null, rows: [] };
  }

  if (Array.isArray(result)) {
    return { title: fallbackTitle, minWidth: null, rows: result };
  }

  return {
    title: result.title !== undefined ? result.title : fallbackTitle,
    minWidth: result.minWidth ?? null,
    rows: Array.isArray(result.rows) ? result.rows : [],
  };
}

function ClrChartTooltip({
  active,
  payload,
  label,
  formatter,
  clampToCard = false,
}) {
  const wrapRef = useRef(null);
  const [nudge, setNudge] = useState({ x: 0, y: 0 });
  const [arrowLeft, setArrowLeft] = useState(null);

  useLayoutEffect(() => {
    if (!clampToCard || !active) {
      setNudge((prev) => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 }));
      setArrowLeft((prev) => (prev == null ? prev : null));
      return undefined;
    }

    const tip = wrapRef.current;
    const card = tip?.closest('.clr-chart-card');
    if (!tip || !card) return undefined;

    const tipBox = tip.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const minLeft = cardBox.left + TOOLTIP_EDGE_GAP;
    const maxRight = cardBox.right - TOOLTIP_EDGE_GAP;
    const minTop = cardBox.top + TOOLTIP_EDGE_GAP;
    const maxBottom = cardBox.bottom - TOOLTIP_EDGE_GAP;

    const naturalLeft = tipBox.left - nudge.x;
    const naturalRight = tipBox.right - nudge.x;
    const naturalTop = tipBox.top - nudge.y;
    const naturalBottom = tipBox.bottom - nudge.y;

    let nextX = 0;
    let nextY = 0;
    if (naturalLeft < minLeft) nextX = minLeft - naturalLeft;
    else if (naturalRight > maxRight) nextX = maxRight - naturalRight;
    if (naturalTop < minTop) nextY = minTop - naturalTop;
    else if (naturalBottom > maxBottom) nextY = maxBottom - naturalBottom;

    setNudge((prev) => {
      if (Math.abs(prev.x - nextX) < 0.5 && Math.abs(prev.y - nextY) < 0.5) return prev;
      return { x: nextX, y: nextY };
    });

    const width = tipBox.width;
    const settledLeft = naturalLeft + nextX;
    const pointX = settledLeft + (width / 2) - nextX;
    const rawArrow = pointX - settledLeft;
    const minArrow = ARROW_EDGE_INSET;
    const maxArrow = Math.max(minArrow, width - ARROW_EDGE_INSET);
    const nextArrow = Math.min(Math.max(rawArrow, minArrow), maxArrow);

    setArrowLeft((prev) => (prev != null && Math.abs(prev - nextArrow) < 0.5 ? prev : nextArrow));
    return undefined;
  }, [active, clampToCard, label, nudge.x, nudge.y, payload]);

  if (!active || !payload?.length) return null;

  const { title, rows, minWidth } = normalizeTooltipContent(payload, label, formatter);
  if (!rows?.length) return null;

  // Value-only tooltips (e.g. Closure time distribution Figma): one row, no label.
  const valueOnly = rows.length === 1 && !rows[0]?.label && (title == null || title === '');

  const wrapStyle = {
    ...(valueOnly && minWidth != null
      ? { ['--clr-tooltip-min-width']: `${minWidth}px` }
      : null),
    ...(clampToCard
      ? {
        transform: `translate(calc(-50% + ${nudge.x}px), calc(-100% - 15px + ${nudge.y}px))`,
      }
      : null),
  };

  return (
    <div
      ref={wrapRef}
      className={`clr-chart-tooltip-wrap${valueOnly ? ' clr-chart-tooltip-wrap--value-only' : ''}`}
      style={Object.keys(wrapStyle).length ? wrapStyle : undefined}
    >
      <div className={`clr-chart-tooltip${valueOnly ? ' clr-chart-tooltip--value-only' : ''}`}>
        {title ? <p className="clr-chart-tooltip-title">{title}</p> : null}
        <div className="clr-chart-tooltip-rows">
          {rows.map((row) => (
            <div key={row.key || row.label || String(row.value)} className="clr-chart-tooltip-row">
              {row.color ? (
                <span
                  className={`clr-chart-tooltip-swatch${row.dashed ? ' clr-chart-tooltip-swatch-dashed' : ' clr-chart-tooltip-swatch-solid'}`}
                  style={{ '--clr-tooltip-swatch': row.color }}
                  aria-hidden="true"
                >
                  <span className="clr-chart-tooltip-swatch-line" />
                  <span className="clr-chart-tooltip-swatch-dot" />
                </span>
              ) : null}
              {row.label ? <span className="clr-chart-tooltip-label">{row.label}:</span> : null}
              <span className="clr-chart-tooltip-value">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
      <TooltipPointer left={clampToCard ? arrowLeft : null} />
    </div>
  );
}

ClrChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formatter: PropTypes.func,
  clampToCard: PropTypes.bool,
};

export default ClrChartTooltip;
